import React from "react";
import { ReactComponent as SmartGateDeniedIcon } from "assets/images/smart-gate-denied.svg";
import { Box, SecondaryButton, Text } from "components";
import useWindowDimensions from "hooks/windowDimensions";
import { useTranslation } from "react-i18next";
import { getTranslationsStyle, isArabic } from "utils/arabisation.util";

import { CircleButtonStyled } from "./ValidationDrawer";

/**
 * Displayed when the passport is valid but the traveller is NOT eligible for Smart Gate.
 * Shows a denied icon, explanation text, and pro tip for next time.
 */
const NotEligibleContent = ({ onContinue, onScanAnother }) => {
  const { t, i18n } = useTranslation();
  const { width } = useWindowDimensions();
  const shouldApplyArabicLayout = isArabic(i18n);

  const calculateScale = () => {
    const scale = 1 + (0.3 * (width - 375)) / (414 - 375);
    return Math.min(1.3, Math.max(1, scale));
  };

  return (
    <Box d="flex" flex="1" direction="column" h="100%" overflow="hidden">
      <Box
        h="48px"
        backgroundColor="smartGateDenied"
        d="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Text
          fontSize="l"
          color="textQuaternary"
          fontWeight="bold"
          style={getTranslationsStyle(i18n)}
        >
          {t("smart_gate.access_denied")}
        </Text>
      </Box>
      <Box
        h="100%"
        flex="1"
        d="flex"
        direction="column"
        justifyContent="center"
        b
      >
        <Box
          flex="1"
          d="flex"
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            d="flex"
            alignItems="center"
            justifyContent="center"
            maxW="200px"
            flex="0.3"
            mT="m"
          >
            <Text
              fontSize="l"
              fontWeight="bold"
              textAlign="center"
              style={getTranslationsStyle(i18n)}
            >
              {t("smart_gate.not_eligible_title")}
            </Text>
          </Box>
          <Box flex="1" d="flex" alignItems="center" justifyContent="center">
            <SmartGateDeniedIcon
              style={{
                transform: `scale(${calculateScale() - 0.2}) translateY(-15px)`,
              }}
            />
          </Box>
        </Box>
        <Box
          d="flex"
          direction="column"
          justifyContent="flex-end"
          style={{ zIndex: 2 }}
          h="100%"
          flex="1"
        >
          <Box
            d="flex"
            direction="column"
            alignItems="center"
            justifyContent="center"
            w="100vw"
            h="80%"
          >
            <Box
              d="flex"
              direction="column"
              gap="s"
              justifyContent="center"
              textAlign="center"
              w="80vw"
              position="relative"
            >
              <Text
                fontSize="l"
                fontWeight="bold"
                textAlign="left"
                color="textQuaternary"
                style={getTranslationsStyle(i18n)}
                position="relative"
              >
                <CircleButtonStyled isArabic={shouldApplyArabicLayout} />
                {t("smart_gate.pro_tip")}
              </Text>
              <Text
                fontSize="l"
                color="textQuaternary"
                style={getTranslationsStyle(i18n)}
              >
                {t("smart_gate.not_eligible_description")}
              </Text>
            </Box>
          </Box>
          <Box
            d="flex"
            justifyContent="center"
            w="100%"
            mT="l"
            style={{ zIndex: 2, marginBottom: "10%" }}
          >
            <Box w="80vw" d="flex" gap="base">
              <SecondaryButton
                w="100%"
                onClick={onContinue}
                style={{ backgroundColor: "#9898BB", borderColor: "#9898BB" }}
              >
                <Text
                  fontSize="m"
                  color="textQuaternary"
                  style={getTranslationsStyle(i18n, { textAlign: "center" })}
                >
                  {t("smart_gate.continue")}
                </Text>
              </SecondaryButton>
              <SecondaryButton w="100%" onClick={onScanAnother}>
                <Text
                  fontSize="m"
                  style={getTranslationsStyle(i18n, { textAlign: "center" })}
                >
                  {t("smart_gate.scan_another")}
                </Text>
              </SecondaryButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NotEligibleContent;
